import { Component, HostBinding, OnInit, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import {
  SiaI18nDirective,
  SiaTranslatePipe,
  SiaUtilsService,
  notify,
} from '@sia/shared';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-restore-password',
  templateUrl: './auth-restore.component.html',
  styleUrls: ['./auth-restore.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButton,
    SiaI18nDirective,
    SiaTranslatePipe,
    RouterModule,
  ],
})
export class AuthRestoreComponent implements OnInit {
  utilsService = inject(SiaUtilsService);
  router = inject(Router);
  fb = inject(FormBuilder);
  service = inject(AuthService);

  @HostBinding('class') hostClasses = 'w-full px-4 lg:px-0';

  loginForm: FormGroup = new FormGroup({
    userIdentifier: new FormControl(''),
    password: new FormControl(''),
    repeatPassword: new FormControl(''),
  });

  credentials = signal<any>({});
  isEmailRequired = signal(true);
  isPasswordRequired = signal(true);
  minLength = signal(5);
  maxLength = signal(20);
  loginRed = signal(false);
  _redirectDelay = signal(300);
  hidePwd = signal(true);
  hideRepeatPwd = signal(true);

  private patternValidator(
    regex: RegExp,
    error: ValidationErrors,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }
  private passwordMatchValidator(control: AbstractControl) {
    const password: string = control.get('password')?.value;
    const confirmPassword: string = control.get('repeatPassword')?.value;
    if (password !== confirmPassword) {
      control.get('repeatPassword')?.setErrors({ NoPassswordMatch: true });
    }
  }
  createSignupForm(): FormGroup {
    const email = this.router.parseUrl(this.router.url).queryParams['email'];
    var form = this.fb.group(
      {
        userIdentifier: [null, Validators.compose([null, Validators.required])],
        password: [
          null,
          Validators.compose([
            Validators.required,
            this.patternValidator(/\d/, { hasNumber: true }),

            this.patternValidator(
              /[!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|]+/,
              { hasSpecialCharacters: true },
            ),
            Validators.minLength(8),
          ]),
        ],
        repeatPassword: [null, Validators.compose([Validators.required])],
      },
      {
        validator: this.passwordMatchValidator,
      },
    );
    if (email) {
      form.get('userIdentifier')?.setValue(email);
    }
    return form;
  }

  ngOnInit() {
    this.loginRed.set(false);
    this.loginForm = this.createSignupForm();
  }

  async restore() {
    if (!this.loginForm.valid) {
      notify(
        'Le credenziali non soddisfano i requisiti indicati !',
        1500,
        'error',
      );
      return;
    }
    this.credentials.set(this.loginForm.value);
    try {
      const data = await this.service.restorePassword(
        'email',
        this.credentials(),
      );
      if (data && data.token) {
        this.router.navigateByUrl('#/auth/login');
      } else {
        notify(
          'Credenziali non aggiornate a causa di un errore o link scaduto.',
          5000,
          'error',
        );
      }
    } catch (e: any) {
      this.utilsService.notifyErrorOnException(e, 'auth-restore');
    }
  }

  passwordToggle(event: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    this.hidePwd.update((val) => !val);
  }

  passwordRepeatToggle(event: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    this.hideRepeatPwd.update((val) => !val);
  }
}
